import React, { useState, useEffect, useCallback } from 'react';
import {
  getUpcomingEvents,
  getMemberAllRegistrations,
  registerForEvent,
  getRegistrationCountByType,
  generateICS,
  EVENT_CATEGORIES,
  fmtDateTime,
} from '../../../api/eventsApi';

// ── Constants ─────────────────────────────────────────────────────────────────
const CATEGORY_COLORS = {
  CIRCLE_TIME:          '#0D9488',
  TALK_WORKSHOP:        '#4338CA',
  SOCIAL_RECREATIONAL:  '#F97316',
  OTHER:                '#6B7280',
};

const CATEGORY_LABEL = Object.fromEntries(
  EVENT_CATEGORIES.map((c) => [c.value, c.label])
);

// ── Helper: download ICS ──────────────────────────────────────────────────────
function downloadICS(event) {
  const ics = generateICS(event);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(event.title || 'event').replace(/\s+/g, '_')}.ics`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── EventCard ─────────────────────────────────────────────────────────────────
function EventCard({ event, registration, counts, onRegister }) {
  const [expanded, setExpanded] = useState(false);

  const color = CATEGORY_COLORS[event.category] || CATEGORY_COLORS.OTHER;
  const spotsLeft = counts ? Math.max(0, (event.member_capacity ?? 0) - counts.member_confirmed) : null;
  const isFull = counts && spotsLeft === 0;

  const status = registration?.status;
  const isConfirmed   = status === 'CONFIRMED'  || status === 'CHECKED_IN';
  const isWaitlisted  = status === 'WAITLISTED';

  return (
    <div
      className="bb-member-card"
      style={{ padding: 0, overflow: 'hidden' }}
    >
      {/* Category colour banner */}
      <div style={{ background: color, height: 6 }} />

      <div style={{ padding: '14px 16px' }}>
        {/* Category + tags row */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8, alignItems: 'center' }}>
          <span
            className="bb-member-badge"
            style={{ background: color, color: '#fff', border: 'none' }}
          >
            {CATEGORY_LABEL[event.category] || event.category}
          </span>
          {(event.tags || []).map((tag) => (
            <span
              key={tag}
              className="bb-member-badge bb-member-badge--grey"
              style={{ fontSize: 11 }}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Title */}
        <div className="bb-member-card-title" style={{ fontSize: 15 }}>
          {event.title}
        </div>

        {/* Date / time */}
        <div className="bb-member-card-meta" style={{ marginTop: 4 }}>
          <span>{fmtDateTime(event.start_datetime)}</span>
          {event.location?.name && (
            <span style={{ marginLeft: 10 }}>{event.location.name}</span>
          )}
        </div>

        {/* Speaker (Talk & Workshop) */}
        {event.category === 'TALK_WORKSHOP' && event.speaker?.name && (
          <div style={{ fontSize: 12, color: '#4338CA', marginTop: 4 }}>
            Speaker: <strong>{event.speaker.name}</strong>
          </div>
        )}

        {/* Capacity bar */}
        {counts !== null && (
          <div style={{ marginTop: 10 }}>
            {isFull ? (
              <span className="bb-member-badge bb-member-badge--amber">Waitlist available</span>
            ) : (
              <span style={{ fontSize: 12, color: '#64748B' }}>
                {spotsLeft} spot{spotsLeft !== 1 ? 's' : ''} left
              </span>
            )}
          </div>
        )}

        {/* Action row */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 }}>
          {isConfirmed ? (
            <span className="bb-member-badge bb-member-badge--green">Registered</span>
          ) : isWaitlisted ? (
            <span className="bb-member-badge bb-member-badge--amber">On Waitlist</span>
          ) : (
            <button
              className="bb-btn-primary"
              style={{
                background: '#16A34A',
                border: 'none',
                color: '#fff',
                padding: '6px 16px',
                borderRadius: 6,
                fontSize: 13,
                cursor: 'pointer',
              }}
              onClick={() => onRegister(event)}
            >
              Register
            </button>
          )}
          <button
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              fontSize: 12,
              color: '#94A3B8',
              cursor: 'pointer',
            }}
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'Show less ▲' : 'Show more ▼'}
          </button>
        </div>

        {/* Expanded details */}
        {expanded && (
          <div style={{ marginTop: 14, borderTop: '1px solid #E2E8F0', paddingTop: 12 }}>
            {event.description && (
              <p style={{ fontSize: 13, color: '#475569', marginBottom: 10, whiteSpace: 'pre-wrap' }}>
                {event.description}
              </p>
            )}
            {event.location?.address && (
              <div style={{ fontSize: 12, color: '#64748B', marginBottom: 6 }}>
                Address: {event.location.address}
                {event.location.postal_code ? `, ${event.location.postal_code}` : ''}
              </div>
            )}
            {event.category === 'TALK_WORKSHOP' && event.speaker?.bio && (
              <div style={{ fontSize: 12, color: '#64748B', marginBottom: 6 }}>
                <strong>About the speaker:</strong> {event.speaker.bio}
              </div>
            )}
            <button
              style={{
                background: 'none',
                border: '1px solid #CBD5E1',
                borderRadius: 6,
                padding: '5px 12px',
                fontSize: 12,
                cursor: 'pointer',
                color: '#475569',
                marginTop: 6,
              }}
              onClick={(e) => { e.stopPropagation(); downloadICS(event); }}
            >
              + Add to Calendar
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Photo Consent Modal ───────────────────────────────────────────────────────
function PhotoConsentModal({ eventTitle, onConsent, onCancel }) {
  return (
    <div className="bb-modal-overlay">
      <div className="bb-modal" style={{ maxWidth: 420 }}>
        <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 10 }}>Photography Consent</h3>
        <p style={{ fontSize: 14, color: '#475569', marginBottom: 20 }}>
          This event involves photography. Do you consent to being photographed at{' '}
          <strong>{eventTitle}</strong>?
        </p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            className="bb-btn-primary"
            style={{ background: '#6B7280', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="bb-btn-primary"
            style={{ background: '#DC2626', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}
            onClick={() => onConsent(false)}
          >
            No, opt out
          </button>
          <button
            className="bb-btn-primary"
            style={{ background: '#16A34A', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}
            onClick={() => onConsent(true)}
          >
            Yes, I consent
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
function EventsDiscoverMember({ orgId, memberId, memberName, memberEmail }) {
  const [events, setEvents]               = useState([]);
  const [regMap, setRegMap]               = useState({});   // eventId → registration
  const [countsMap, setCountsMap]         = useState({});   // eventId → { confirmed, waitlisted }
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState('');

  // Filter state
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [searchKeyword, setSearchKeyword]   = useState('');

  // Registration flow
  const [pendingEvent, setPendingEvent]   = useState(null);  // event awaiting consent
  const [registering, setRegistering]     = useState(false);
  const [toast, setToast]                 = useState('');

  // ── Load on mount ──────────────────────────────────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [eventsData, regsData] = await Promise.all([
        getUpcomingEvents(orgId, 90),
        memberId ? getMemberAllRegistrations(orgId, memberId) : Promise.resolve([]),
      ]);

      setEvents(eventsData);

      // Build reg map
      const map = {};
      regsData.forEach((r) => {
        if (r.status !== 'CANCELLED') {
          map[r.event_id] = r;
        }
      });
      setRegMap(map);

      // Fetch counts for each event (split by registrant type so member spots are accurate)
      const counts = {};
      await Promise.all(
        eventsData.map(async (ev) => {
          try {
            counts[ev.id] = await getRegistrationCountByType(orgId, ev.id);
          } catch (_) {
            counts[ev.id] = null;
          }
        })
      );
      setCountsMap(counts);
    } catch (err) {
      setError(err.message || 'Failed to load events.');
    } finally {
      setLoading(false);
    }
  }, [orgId, memberId]);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Toast helper ──────────────────────────────────────────────────────────
  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 4000);
  };

  // ── Register flow ─────────────────────────────────────────────────────────
  const handleRegisterClick = (event) => {
    if (event.photo_consent_required) {
      setPendingEvent(event);
    } else {
      doRegister(event, null);
    }
  };

  const handleConsentChoice = (consent) => {
    const event = pendingEvent;
    setPendingEvent(null);
    doRegister(event, consent);
  };

  const doRegister = async (event, photoConsent) => {
    setRegistering(true);
    try {
      const result = await registerForEvent(
        orgId,
        event.id,
        memberId,
        memberName,
        memberEmail,
        memberId,
        photoConsent
      );
      if (result.status === 'CONFIRMED') {
        showToast('You\'re registered! ✓');
      } else {
        showToast(`Added to waitlist — position #${result.waitlist_position}`);
      }
      // Refresh reg map and counts for this event
      const [newReg, newCount] = await Promise.all([
        getMemberAllRegistrations(orgId, memberId),
        getRegistrationCountByType(orgId, event.id),
      ]);
      const map = { ...regMap };
      newReg.forEach((r) => {
        if (r.status !== 'CANCELLED') map[r.event_id] = r;
      });
      setRegMap(map);
      setCountsMap((prev) => ({ ...prev, [event.id]: newCount }));
    } catch (err) {
      showToast(err.message || 'Registration failed.');
    } finally {
      setRegistering(false);
    }
  };

  // ── Filtering ─────────────────────────────────────────────────────────────
  const filteredEvents = events.filter((ev) => {
    if (categoryFilter !== 'ALL' && ev.category !== categoryFilter) return false;
    if (searchKeyword.trim()) {
      const kw = searchKeyword.toLowerCase();
      const inTitle = ev.title?.toLowerCase().includes(kw);
      const inDesc  = ev.description?.toLowerCase().includes(kw);
      const inTags  = (ev.tags || []).some((t) => t.toLowerCase().includes(kw));
      if (!inTitle && !inDesc && !inTags) return false;
    }
    return true;
  });

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 40 }}>
        <div className="bb-loading-spinner" />
      </div>
    );
  }

  return (
    <div>
      <h2 className="bb-member-section-title">Discover Events</h2>
      <p className="bb-member-section-sub">Browse upcoming events and register your place.</p>

      {error && <div className="bb-alert bb-alert--error">{error}</div>}

      {/* Filter bar */}
      <div className="bb-member-filter-bar" style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 20, alignItems: 'center' }}>
        <select
          className="bb-select"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          style={{ padding: '7px 12px', fontSize: 13 }}
        >
          <option value="ALL">All Categories</option>
          {EVENT_CATEGORIES.map((c) => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>

        <input
          className="bb-input"
          type="text"
          placeholder="Search events..."
          value={searchKeyword}
          onChange={(e) => setSearchKeyword(e.target.value)}
          style={{ padding: '7px 12px', fontSize: 13, minWidth: 200 }}
        />
      </div>

      {/* Events grid */}
      {filteredEvents.length === 0 ? (
        <div className="bb-empty-state" style={{ textAlign: 'center', padding: 40, color: '#64748B' }}>
          <p>No upcoming events found{categoryFilter !== 'ALL' || searchKeyword ? ' matching your filters' : ''}.</p>
        </div>
      ) : (
        <div className="bb-member-grid">
          {filteredEvents.map((ev) => (
            <EventCard
              key={ev.id}
              event={ev}
              registration={regMap[ev.id] || null}
              counts={countsMap[ev.id] ?? null}
              onRegister={handleRegisterClick}
            />
          ))}
        </div>
      )}

      {/* Photo consent modal */}
      {pendingEvent && (
        <PhotoConsentModal
          eventTitle={pendingEvent.title}
          onConsent={handleConsentChoice}
          onCancel={() => setPendingEvent(null)}
        />
      )}

      {/* Registering overlay */}
      {registering && (
        <div className="bb-modal-overlay" style={{ background: 'rgba(0,0,0,0.25)' }}>
          <div style={{ background: '#fff', borderRadius: 10, padding: 30, textAlign: 'center' }}>
            <div className="bb-loading-spinner" />
            <p style={{ marginTop: 12, color: '#475569' }}>Registering...</p>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className="bb-member-toast"
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            background: '#1E293B',
            color: '#fff',
            padding: '12px 20px',
            borderRadius: 8,
            fontSize: 14,
            zIndex: 9999,
            boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          }}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

export default EventsDiscoverMember;
